// Lazy migration (APP-3155): serialize an unmigrated dashboard's live Redux state into a new layout
// + instance. Pure orchestration — the hook injects the create fns + store, so this is testable without React.
import { exportLayoutConfig } from "./export-layout-config"
import { serializeWorkspace, type WorkspaceSerializableState } from "./serialize-workspace"
import type { Uuid } from "helpers/types/uuid"
import { instantiateInstanceFromLayout } from "./instantiate-instance-from-layout"
import type { Layout } from "resources/layouts"
import type { DashboardInstance, CreateInstancePayload } from "resources/dashboard-instances"
import type { LayoutConfigV1, WorkspaceStateV1 } from "../contracts/layouts"
import { DEFAULT_DASHBOARD_ID } from "../context/dashboard-id"

// Generic name for the legacy "default" dashboard, which had no user-facing name.
export const MIGRATED_LAYOUT_NAME = "My dashboard"

/**
 * The name for the layout + instance minted from an existing dashboard.
 *
 * "default" had no name, so it gets the generic one. A slug dashboard's slug IS the user's name —
 * titleize it ("my-portfolio" → "My Portfolio"); a slug with no word characters falls back to generic.
 */
export function inferMigratedDashboardName(dashboardId: string): string {
  if (dashboardId === DEFAULT_DASHBOARD_ID) return MIGRATED_LAYOUT_NAME
  const words = dashboardId.split(/[-_\s]+/).filter(Boolean)
  if (words.length === 0) return MIGRATED_LAYOUT_NAME
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
}

// Module-level run-once guard keyed by dashboard-key (NOT a React ref, which re-arms per remount).
// Failure deletes the key so the migration can retry in-session; success leaves it in place.
const migrationsInFlight = new Set<string>()

// Exported for tests: clears the module-level guard between cases.
export function resetLazyMigrationGuard(): void {
  migrationsInFlight.clear()
}

// Claims the key synchronously (same tick, before any await) so two concurrent first-loads can't
// both start a migration. Returns false if the key was already claimed.
export function claimMigration(dashboardKey: string): boolean {
  if (migrationsInFlight.has(dashboardKey)) return false
  migrationsInFlight.add(dashboardKey)
  return true
}

// Releases the key on failure so a transient 500 doesn't wedge the key for the whole session.
export function releaseMigration(dashboardKey: string): void {
  migrationsInFlight.delete(dashboardKey)
}

export interface MigrateDashboardArgs {
  // Satisfies both serializeWorkspace and exportLayoutConfig.
  state: WorkspaceSerializableState
  dashboardId: string
  // Verbatim persisted Dockview arrangement (dashboard.dockviewLayout), or null for panels-only.
  arrangement: Record<string, unknown> | null
  // Persisted model id (dashboard.model), read directly not from the model slice (its
  // isPreferencesLoaded can flip true via the empty-prefs fallback before the real value lands). null → no saved model.
  modelId: string | null
  createLayout: (name: string, config: LayoutConfigV1) => Promise<Layout>
  createInstance: (payload: CreateInstancePayload) => Promise<DashboardInstance>
  saveWorkspace: (instanceId: string, workspace: WorkspaceStateV1) => Promise<void>
}

/**
 * Serialize the existing dashboard and create its layout + instance.
 *
 * Never throws on corrupt/sparse slice state (missing slices default to empty); only the network
 * POSTs propagate failures.
 *
 * @returns the new instanceId + server-generated slug — the caller writes the migrated-flag row and routes to the slug.
 */
export async function migrateDashboardToInstance({
  state,
  dashboardId,
  arrangement,
  modelId,
  createLayout,
  createInstance,
  saveWorkspace,
}: MigrateDashboardArgs): Promise<{ instanceId: Uuid, slug: string }> {
  const { config } = exportLayoutConfig(state, dashboardId, arrangement)
  const workspace = serializeWorkspace(state, dashboardId, arrangement)
  // The directly-read persisted modelId is authoritative over serializeWorkspace's (possibly still-loading) slice value.
  if (modelId != null) {
    workspace.core = { ...workspace.core, modelId }
  }

  const name = inferMigratedDashboardName(dashboardId)
  // Delegate the create tail (layout + instance + best-effort workspace PUT) to the shared
  // instantiate fn so the subtle "a failed PUT must not reject" contract lives in one place. [APP-3266]
  return instantiateInstanceFromLayout({
    name,
    config,
    workspace,
    createLayout,
    createInstance,
    saveWorkspace,
  })
}
